import { callApi } from "@/api/callApi";
import { API_ENDPOINT } from "@/api/constant";
import type {
  EmployeeOnBoard,
  EmployeeOnBoardResponse,
  IEmployeeDetailsResponse,
  IUserListResponse,
} from "@/interface/employeeOnBoard";
import type {
  IBulkImportPreviewResponse,
  IBulkImportResponse,
  IBulkImportRow,
} from "@/interface/bulkImport.interface";
import type {
  IBranchWiseSalaryHeadResponse,
  IUserSalaryOverviewResponse,
} from "@/interface/salaryHead.interface";

const EmployeeOnBoardService = {
  createEmployee: async (
    payload: EmployeeOnBoard,
  ): Promise<EmployeeOnBoardResponse> => {
    const API = `${API_ENDPOINT.user}/onboard`;
    return callApi<EmployeeOnBoardResponse>(API, {
      method: "POST",
      data: payload,
    });
  },
  editEmployee: async (
    payload: Partial<EmployeeOnBoard>,
    id: string,
  ): Promise<EmployeeOnBoardResponse> => {
    const API = `${API_ENDPOINT.user}/${id}`;
    return callApi<EmployeeOnBoardResponse>(API, {
      method: "PUT",
      data: payload,
    });
  },
  getAllEmployees: async (params?: string): Promise<IUserListResponse> => {
    const API = `${API_ENDPOINT.user}/get-employee${params ? `?${params}` : ""}`;
    return callApi<IUserListResponse>(API, { method: "GET" });
  },
  getEmployeeById: async (id: string): Promise<IEmployeeDetailsResponse> => {
    const API = `${API_ENDPOINT.user}/${id}`;
    return callApi<IEmployeeDetailsResponse>(API, { method: "GET" });
  },

  updateTeamLead: async (employeeId: string, teamLeadId: string | null) => {
    const API = `${API_ENDPOINT.user}/${employeeId || ""}/team-lead`;
    return callApi(API, {
      method: "PUT",
      data: { teamLeadId: teamLeadId || "" },
    });
  },

  saveEmployeeDocuments: async (
    userId: string,
    payload: {
      documents: { type: string; url: string; name: string }[];
    },
  ) => {
    const API = `${API_ENDPOINT.user}/${userId}/documents`;
    return callApi(API, {
      method: "POST",
      data: payload,
    });
  },

  getEmployeeDocuments: async (userId: string) => {
    const API = `${API_ENDPOINT.user}/${userId}/documents`;
    return callApi(API, {
      method: "GET",
    });
  },

  deleteEmployeeDocument: async (documentId: string) => {
    const API = `${API_ENDPOINT.user}/documents/${documentId}`;
    return callApi(API, {
      method: "DELETE",
    });
  },

  // ── Bulk Import ────────────────────────────────────────

  /** Download the Excel template for bulk employee import */
  downloadImportTemplate: async (): Promise<Blob> => {
    const API = `${API_ENDPOINT.user}/import/template`;
    return callApi<Blob>(API, { method: "GET" });
  },

  /** Upload a filled Excel file and get a preview of parsed rows */
  previewImport: async (
    formData: FormData,
  ): Promise<IBulkImportPreviewResponse> => {
    const API = `${API_ENDPOINT.user}/import/preview`;
    return callApi<IBulkImportPreviewResponse>(API, {
      method: "POST",
      data: formData,
      multipart: true,
    });
  },

  /** Submit validated rows for final bulk import via Excel FormData */
  bulkImport: async (
    payload: FormData | { rows: IBulkImportRow[] } | IBulkImportRow[],
  ): Promise<IBulkImportResponse> => {
    const API = `${API_ENDPOINT.user}/import`;
    const isFormData = payload instanceof FormData;
    return callApi<IBulkImportResponse>(API, {
      method: "POST",
      data: isFormData
        ? payload
        : Array.isArray(payload)
          ? { rows: payload }
          : payload,
      multipart: true,
    });
  },

  saveUserSalary: async (
    userId: string,
    annualSalary: number,
  ): Promise<any> => {
    const API = `/user-salary?userId=${userId}`;
    return callApi(API, {
      method: "POST",
      data: { annualSalary },
    });
  },

  getUserSalaryOverview: async (
    userId: string,
  ): Promise<IUserSalaryOverviewResponse> => {
    const API = `/user-salary-overview?userId=${userId}`;
    return callApi<IUserSalaryOverviewResponse>(API, {
      method: "GET",
    });
  },

  generateOfferLetter: async (userId: string): Promise<ArrayBuffer> => {
    const API = `/generate-offer-latter?userId=${userId}`;
    return callApi<ArrayBuffer>(API, {
      method: "GET",
    });
  },

  getBranchSalaryHeads: async (params: {
    type?: "earning" | "deduction";
    branchId?: string;
    pageNumber?: number;
    pageSize?: number;
    search?: string;
  }): Promise<IBranchWiseSalaryHeadResponse> => {
    const query = new URLSearchParams();
    if (params.type) query.append("type", params.type);
    if (params.branchId) query.append("branchId", params.branchId);
    if (params.pageNumber !== undefined)
      query.append("pageNumber", String(params.pageNumber));
    if (params.pageSize !== undefined)
      query.append("pageSize", String(params.pageSize));
    if (params.search) query.append("search", params.search);
    const queryString = query.toString();
    const API = `/branch-salary-head${queryString ? `?${queryString}` : ""}`;
    return callApi<IBranchWiseSalaryHeadResponse>(API, { method: "GET" });
  },

  saveUserSalaryHeads: async (
    userId: string,
    userSalary: { branchSalaryHeadId: string; amount: number }[],
  ) => {
    const API = `/user-salary?userId=${userId}`;
    return callApi(API, {
      method: "POST",
      data: { userSalary },
    });
  },
};

export default EmployeeOnBoardService;
